"use client"
import { Button } from "@/components/ui/button";
import { CustomSheetContent, Sheet, SheetClose, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { CircleUser, ClipboardList, IndianRupee, LayoutDashboard, Menu, X, } from "lucide-react";
import Link from "next/link";
import { usePathname } from 'next/navigation';
import { ScrollArea } from "@/components/ui/scroll-area";
import UserButton from "@/components/global/user-button";
import { LOGO } from "@/lib/constant";
import Image from "next/image";

export default function UserLayout({ children }: { children: React.ReactNode }) {
    const pathname = usePathname()

    const userNav = [
        {
            href: "/user-dashboard",
            label: "Dashboard",
            icon: <LayoutDashboard className="size-4 lg:size-5" />
        },
        {
            href: "/user-dashboard/profile",
            label: "Profile",
            icon: <CircleUser className="size-4 lg:size-5" />
        },
        {
            href: "/user-dashboard/bookings",
            label: "Bookings",
            icon: <ClipboardList className="size-4 lg:size-5" />
        },
        {
            href: "/user-dashboard/payment-options",
            label: "Payment Options",
            icon: <IndianRupee className="size-4 lg:size-5" />
        }
    ]

    // Function to determine if a nav item should be active
    const isActiveNavItem = (href: string) => {
        if (pathname === href) return true;

        // Find all matching nav items
        const matchingNavs = userNav.filter(nav => pathname.includes(nav.href));

        // If no matches, return false
        if (matchingNavs.length === 0) return false;

        // Return true only for the longest matching href
        const longestMatch = matchingNavs.reduce((prev, current) =>
            current.href.length > prev.href.length ? current : prev
        );

        return href === longestMatch.href;
    }

    return (
        <div className="grid min-h-screen overflow-y-scroll w-full md:grid-cols-[220px_1fr] lg:grid-cols-[280px_1fr]">
            <div className="hidden bg-muted/40 md:block">
                <div className="flex h-full max-h-screen flex-col gap-2 bg-custom-primary-dark">
                    <div className="flex h-14 items-center border-b border-white/20 px-4 lg:h-20 lg:px-6">
                        <div className="relative h-16 lg:h-20 w-40">
                            <Link href={"/"} className='h-full w-full'>
                                <Image src={LOGO} alt="Trek Karo" fill loading="lazy" quality={50} className='object-contain object-left' />
                            </Link>
                        </div>
                    </div>
                    <div className="flex-1 mt-6">
                        <nav className="grid items-start text-sm lg:text-base font-medium gap-2.5">
                            {
                                userNav.map((opt) => (
                                    <Link
                                        key={opt.href}
                                        href={opt.href}
                                        className={cn("flex items-center gap-3 px-3 lg:px-4 lg:pl-8 py-2 lg:py-4 text-primary hover:text-foreground tracking-wide", isActiveNavItem(opt.href) ? "bg-white" : "bg-none hover:bg-white text-white")}
                                    >
                                        {opt.icon}
                                        {opt.label}
                                    </Link>
                                ))
                            }
                        </nav>
                    </div>
                </div>
            </div>
            <div className="flex flex-col">
                <header className="flex h-14 items-center justify-between md:justify-end  gap-4 border-b border-white/20 px-4 lg:h-20 lg:px-6 bg-custom-primary-dark">
                    <Sheet>
                        <SheetTrigger asChild>
                            <Button
                                variant="outline"
                                size="icon"
                                className="shrink-0 md:hidden"
                            >
                                <Menu className="h-5 w-5" />
                                <span className="sr-only">Toggle navigation menu</span>
                            </Button>
                        </SheetTrigger>
                        <CustomSheetContent side="left" className="flex flex-col bg-custom-primary-dark border-transparent px-0">
                            <nav className="grid gap-2 text-lg font-medium">
                                <div className='h-fit w-full flex flex-row justify-between items-center px-2.5'>
                                    <div className='h-20 w-40 relative'>
                                        <Image src={LOGO} alt='Trek Karo' fill loading="lazy" quality={50} className='object-contain object-left' />
                                    </div>
                                    <SheetClose asChild>
                                        <X className='h-8 w-8 p-1.5 bg-custom-primary rounded-full text-white' />
                                    </SheetClose>
                                </div>
                                <div className="px-8 space-y-4">
                                    {
                                        userNav.map((opt) => (
                                            <SheetClose
                                                asChild
                                                key={opt.href}
                                            >
                                                <Link
                                                    href={opt.href}
                                                    className={cn("mx-[-0.65rem] flex flex-shrink-0 items-center gap-4 rounded-md px-3 py-2 border", isActiveNavItem(opt.href) ? "bg-white border-neutral-900 text-zinc-900 font-normal" : "bg-transparent text-white font-normal border-white")}
                                                >
                                                    {opt.icon}
                                                    {opt.label}
                                                </Link>
                                            </SheetClose>
                                        ))
                                    }
                                </div>
                            </nav>
                        </CustomSheetContent>
                    </Sheet>
                    <UserButton />
                </header>
                <ScrollArea className="h-[89vh]">
                    {children}
                </ScrollArea>
            </div>
        </div>
    )
}